import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { requireAuth } from '../features/auth/guards.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@tanstack/react-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { PageHeader } from '@/components/account/PageHeader.js';
import { BankAccountsSection } from '@/components/account/BankAccountsSection.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { PasswordInput } from '@/components/ui/password-input.js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import { Money } from '@/components/money/Money.js';
import { useAuthStore } from '@/stores/auth.js';
import { usePrefsStore } from '@/stores/prefs.js';
import {
  fetchMemberMe,
  patchMemberMe,
  changePasswordMutation,
  MEMBER_QUERY_KEYS,
} from '@/features/member/api.js';
import {
  fetchMfaStatus,
  enrollMfa,
  verifyMfaEnroll,
  disableMfa,
  mfaStatusKey,
} from '@/features/security/api.js';
import { mapApiError } from '@/lib/api/error.js';
import { formatDate } from '@/lib/datetime/format.js';
import { WALLET_QUERY_KEYS, fetchWalletBalances } from '@/features/wallet/api.js';
import { SUPPORTED_LOCALES, type Locale } from '@/lib/i18n/index.js';
import { useTranslation } from 'react-i18next';
import { useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import { ResponsibleGamblingSection } from '@/components/account/ResponsibleGamblingSection.js';
import { PASSWORD_MIN_LENGTH } from '@/features/auth/password-policy.js';

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  zh: '中文',
  km: 'ខ្មែរ',
  lo: 'ລາວ',
  my: 'မြန်မာ',
};

export const Route = createFileRoute('/_player/profile')({
  beforeLoad: requireAuth,
  component: ProfileScreen,
});

// ── Schemas ───────────────────────────────────────────────────────────────────

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password required'),
    newPassword: z
      .string()
      .min(PASSWORD_MIN_LENGTH, `New password must be at least ${PASSWORD_MIN_LENGTH} characters`),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'New password must differ from the current one',
    path: ['newPassword'],
  });

type ChangePassword = z.infer<typeof changePasswordSchema>;

// ── Sub-components ────────────────────────────────────────────────────────────

function Avatar({ username, size = 40 }: { username: string; size?: number }) {
  const initials = (username || '').slice(0, 2).toUpperCase();
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <div
        className="absolute rounded-full bg-[var(--bg-elevated)] grid place-items-center text-[var(--text-primary)] font-bold"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          fontSize: size * 0.32,
        }}
      >
        {initials}
      </div>
    </div>
  );
}

function ProfileField({ label, value, type = 'text', readOnly = false, onChange }: {
  label: string;
  value: string;
  type?: string;
  readOnly?: boolean;
  onChange?: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
        {label}
      </label>
      {readOnly ? (
        <div className="h-11 px-3.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)]/40 flex items-center text-[15px] font-semibold text-[var(--text-secondary)]">
          {value}
        </div>
      ) : (
        <Input
          type={type}
          defaultValue={value}
          onChange={(e) => onChange?.(e.target.value)}
        />
      )}
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="flex flex-col gap-6 animate-pulse">
      <div className="h-32 bg-[var(--bg-elevated)]/50 rounded-xl" />
      <div className="h-48 bg-[var(--bg-elevated)]/50 rounded-xl" />
    </div>
  );
}

// ── Add Bank Modal ─────────────────────────────────────────────────────────────

// ── Change Password Modal ──────────────────────────────────────────────────────

function ChangePasswordModal({
  subjectId,
  onClose,
}: {
  subjectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('profile');

  const form = useForm<ChangePassword>({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    onSubmit: async ({ value }) => {
      const parsed = changePasswordSchema.safeParse(value);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0]?.message ?? t('common.validationError'));
        return;
      }
      await pwMutation.mutateAsync(parsed.data);
    },
  });

  const pwMutation = useMutation({
    mutationFn: (values: ChangePassword) =>
      changePasswordMutation({
        subjectId,
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      }),
    onSuccess: () => {
      toast.success(t('changePassword.success'));
      onClose();
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogTitle>{t('changePassword.title')}</DialogTitle>
        <form
          onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }}
          className="flex flex-col gap-3"
        >
          <form.Field name="currentPassword" validators={{ onChange: ({ value }) => {
            return value.length >= 1 ? undefined : t('changePassword.currentRequired');
          }}}>
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{t('changePassword.currentLabel')}</label>
                <Input type="password" placeholder="••••••••" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                {field.state.meta.errors.length > 0 && <span className="text-[11px] text-[var(--danger)]">{field.state.meta.errors[0]}</span>}
              </div>
            )}
          </form.Field>

          <form.Field name="newPassword" validators={{ onChangeListenTo: ['currentPassword'], onChange: ({ value, fieldApi }) => {
            if (value.length < PASSWORD_MIN_LENGTH) {
              return t('changePassword.tooShort', { min: PASSWORD_MIN_LENGTH });
            }
            const current = fieldApi.form.getFieldValue('currentPassword');
            return value === current ? t('changePassword.sameAsCurrent') : undefined;
          }}}>
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{t('changePassword.newLabel')}</label>
                <Input type="password" placeholder={t('changePassword.newPlaceholder', { min: PASSWORD_MIN_LENGTH })} value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                {field.state.meta.errors.length > 0 && <span className="text-[11px] text-[var(--danger)]">{field.state.meta.errors[0]}</span>}
              </div>
            )}
          </form.Field>

          <form.Field name="confirmPassword" validators={{ onChangeListenTo: ['newPassword'], onChange: ({ value, fieldApi }) => {
            const newPw = fieldApi.form.getFieldValue('newPassword');
            return value === newPw ? undefined : t('changePassword.mismatch');
          }}}>
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{t('changePassword.confirmLabel')}</label>
                <Input type="password" placeholder={t('changePassword.confirmPlaceholder')} value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                {field.state.meta.errors.length > 0 && <span className="text-[11px] text-[var(--danger)]">{field.state.meta.errors[0]}</span>}
              </div>
            )}
          </form.Field>

          <div className="flex gap-2 mt-2">
            <Button type="submit" glow fullWidth disabled={pwMutation.isPending}>
              {pwMutation.isPending ? t('changePassword.submitting') : t('changePassword.submit')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>{t('changePassword.cancel')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Two-Factor Enroll Modal ────────────────────────────────────────────────────

function TwoFactorEnrollModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('profile');
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [password, setPassword] = useState('');

  const enrollMutation = useMutation({
    mutationFn: (pw: string) => enrollMfa(pw),
    onError: (err) => {
      // Stay open on a wrong password so the member can retry; any other error
      // closes as before.
      toast.error(mapApiError(err, t));
    },
  });

  // The secret is no longer requested on open: enrolment re-authenticates first
  // (AUT-10), so the member confirms their password and only then is a secret
  // staged. Staged secrets remain inert until verified, so abandoning the flow
  // after that step still costs nothing.

  const verifyMutation = useMutation({
    mutationFn: () => verifyMfaEnroll(code),
    onSuccess: () => {
      toast.success(t('twoFactor.enrollSuccess'));
      void qc.invalidateQueries({ queryKey: mfaStatusKey });
      onClose();
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  const enrollment = enrollMutation.data;

  function copySecret() {
    if (!enrollment) return;
    void navigator.clipboard.writeText(enrollment.secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogTitle>{t('twoFactor.title')}</DialogTitle>
        {!enrollment ? (
          <form
            onSubmit={(e) => { e.preventDefault(); enrollMutation.mutate(password); }}
            className="flex flex-col gap-4"
          >
            <div className="text-[13px] text-[var(--text-secondary)]">
              {t('twoFactor.confirmPasswordHint')}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                {t('twoFactor.passwordLabel')}
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" disabled={enrollMutation.isPending || password.length < 1}>
              {t('twoFactor.continue')}
            </Button>
          </form>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); verifyMutation.mutate(); }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col items-center gap-3">
              <div className="text-[13px] font-semibold text-[var(--text-primary)] self-start">
                {t('twoFactor.scanTitle')}
              </div>
              <div className="p-3 rounded-xl bg-white">
                <QRCodeSVG value={enrollment.otpauthUrl} size={176} />
              </div>
              <div className="text-[12px] text-[var(--text-secondary)] text-center">
                {t('twoFactor.scanHint')}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                {t('twoFactor.manualEntryLabel')}
              </label>
              <div className="flex items-center gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)]/40 px-3 py-2">
                <code className="flex-1 min-w-0 font-mono text-[13px] break-all select-all text-[var(--text-primary)]">
                  {enrollment.secret}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  aria-label={t('twoFactor.copySecret')}
                  className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  {copied ? <Check className="size-4 text-[var(--success)]" /> : <Copy className="size-4" />}
                </button>
              </div>
              {copied && (
                <span className="text-[11px] text-[var(--success)]">{t('twoFactor.copied')}</span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                {t('twoFactor.codeLabel')}
              </label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder={t('twoFactor.codePlaceholder')}
                className="font-mono tracking-[0.3em]"
              />
            </div>

            <div className="flex gap-2 mt-1">
              <Button
                type="submit"
                glow
                fullWidth
                disabled={verifyMutation.isPending || !/^\d{6}$/.test(code)}
              >
                {t('twoFactor.verifySubmit')}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>{t('twoFactor.cancel')}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Two-Factor Disable Modal ───────────────────────────────────────────────────

function TwoFactorDisableModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('profile');
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const disableMutation = useMutation({
    mutationFn: () => disableMfa(password, code),
    onSuccess: () => {
      toast.success(t('twoFactor.disableSuccess'));
      void qc.invalidateQueries({ queryKey: mfaStatusKey });
      onClose();
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogTitle>{t('twoFactor.disableTitle')}</DialogTitle>
        <form
          onSubmit={(e) => { e.preventDefault(); disableMutation.mutate(); }}
          className="flex flex-col gap-3"
        >
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
              {t('twoFactor.passwordLabel')}
            </label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
              {t('twoFactor.codeLabel')}
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder={t('twoFactor.codePlaceholder')}
              className="font-mono tracking-[0.3em]"
            />
          </div>

          <div className="flex gap-2 mt-2">
            <Button
              type="submit"
              glow
              fullWidth
              disabled={disableMutation.isPending || password.length < 1 || !/^\d{6}$/.test(code)}
            >
              {t('twoFactor.disableSubmit')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>{t('twoFactor.cancel')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Screen ───────────────────────────────────────────────────────────────

function ProfileScreen() {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation('profile');
  const { subject, clear } = useAuthStore();
  const memberId = subject?.id ?? '';
  const locale = usePrefsStore((s) => s.locale);
  const setLocale = usePrefsStore((s) => s.setLocale);

  const [showChangePw, setShowChangePw] = useState(false);
  const [showMfaEnroll, setShowMfaEnroll] = useState(false);
  const [showMfaDisable, setShowMfaDisable] = useState(false);

  // Edit state
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLineId, setEditLineId] = useState('');
  const [editDirty, setEditDirty] = useState(false);

  const memberQuery = useQuery({
    queryKey: MEMBER_QUERY_KEYS.me,
    queryFn: fetchMemberMe,
    staleTime: 30_000,
  });

  const walletQuery = useQuery({
    queryKey: WALLET_QUERY_KEYS.balances,
    queryFn: fetchWalletBalances,
    staleTime: 15_000,
  });

  const mfaQuery = useQuery({
    queryKey: mfaStatusKey,
    queryFn: fetchMfaStatus,
    staleTime: 30_000,
  });
  const mfaEnabled = mfaQuery.data?.enabled ?? false;

  const member = memberQuery.data?.member;
  const currency = useMoneyCurrency();

  useEffect(() => {
    if (!member || editDirty) return;
    setEditFirstName(member.firstName ?? '');
    setEditLastName(member.lastName ?? '');
    setEditEmail(member.email ?? '');
    setEditLineId(member.lineId ?? '');
  }, [member, editDirty]);

  const patchMutation = useMutation({
    mutationFn: () =>
      patchMemberMe(subject!.id, {
        firstName: editFirstName || null,
        lastName: editLastName || null,
        email: editEmail || null,
        lineId: editLineId || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MEMBER_QUERY_KEYS.me });
      setEditDirty(false);
      toast.success('Profile updated');
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  const languageMutation = useMutation({
    mutationFn: (next: Locale) =>
      patchMemberMe(subject!.id, { preferredLanguage: next }),
    onSuccess: (_data, next) => {
      setLocale(next);
      void qc.invalidateQueries({ queryKey: MEMBER_QUERY_KEYS.me });
      toast.success(t('language.saved'));
    },
    onError: (err) => toast.error(mapApiError(err, t)),
  });

  if (memberQuery.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('title')} subtitle={t('subtitle')} />
        <ProfileSkeleton />
      </div>
    );
  }

  if (memberQuery.isError || !member) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('title')} subtitle={t('subtitle')} />
        <div className="py-10 text-center text-[var(--danger)] flex flex-col items-center gap-3">
          Failed to load profile.
          <Button size="sm" variant="secondary" onClick={() => memberQuery.refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  // createdAt arrives as a space-separated Postgres timestamp (no 'T'); take the
  // date portion so date-fns parses it cleanly, then format per the active locale.
  const createdDate = (member.createdAt ?? '').split(/[ T]/)[0];
  const memberSince = createdDate ? formatDate(createdDate, i18n.language) : '—';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      />

      {/* Identity card */}
      <Card className="p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-5">
        <Avatar username={member.username} size={72} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-[20px] font-extrabold tracking-tight">{member.username}</h2>
          </div>
          <div className="text-[13px] text-[var(--text-secondary)] mt-1">
            {t('identity.memberSinceLong', { date: memberSince, id: member.id.slice(0, 8) })}
          </div>
        </div>
      </Card>

      {/* Personal details */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('identity.title')}
        </h2>
        <Card className="p-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <ProfileField label={t('identity.username')} value={member.username}  readOnly />
            <ProfileField label={t('identity.phone')}    value={member.phone ?? '—'} readOnly />
            <ProfileField
              label={t('identity.firstName')}
              value={editFirstName}
              onChange={(v) => { setEditFirstName(v); setEditDirty(true); }}
            />
            <ProfileField
              label={t('identity.lastName')}
              value={editLastName}
              onChange={(v) => { setEditLastName(v); setEditDirty(true); }}
            />
            <ProfileField
              label={t('identity.email')}
              value={editEmail}
              type="email"
              onChange={(v) => { setEditEmail(v); setEditDirty(true); }}
            />
            <ProfileField
              label={t('identity.lineId')}
              value={editLineId}
              onChange={(v) => { setEditLineId(v); setEditDirty(true); }}
            />
          </div>
          <div className="mt-5 flex gap-2">
            <Button
              glow
              disabled={patchMutation.isPending}
              onClick={() => patchMutation.mutate()}
            >
              {patchMutation.isPending ? t('identity.saving') : t('identity.saveChanges')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setEditFirstName(member.firstName ?? '');
                setEditLastName(member.lastName ?? '');
                setEditEmail(member.email ?? '');
                setEditLineId(member.lineId ?? '');
                setEditDirty(false);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      </section>

      <BankAccountsSection />

      {/* Language */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('language.title')}
        </h2>
        <Card className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SUPPORTED_LOCALES.map((l) => {
              const active = l === locale;
              return (
                <button
                  key={l}
                  type="button"
                  disabled={languageMutation.isPending}
                  onClick={() => {
                    if (active) return;
                    languageMutation.mutate(l);
                  }}
                  className={[
                    'h-11 px-3 rounded-lg border text-[13px] font-semibold transition-colors flex items-center justify-between disabled:opacity-60',
                    active
                      ? 'border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--text-primary)]'
                      : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]',
                  ].join(' ')}
                >
                  <span>{LOCALE_LABELS[l] ?? l}</span>
                  <span className="font-mono text-[10px] uppercase text-[var(--text-muted)]">{l}</span>
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-[var(--text-muted)] mt-3">
            {t('language.hint')}
          </div>
        </Card>
      </section>

      {/* Wallet summary */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('stats.title')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {walletQuery.isPending ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 bg-[var(--bg-elevated)]/50 animate-pulse rounded-xl" />
            ))
          ) : walletQuery.data ? (
            [
              { label: t('stats.cash'),       value: walletQuery.data.creditCash,       tone: 'var(--success)' },
              { label: t('stats.bonus'),       value: walletQuery.data.creditPromotion,   tone: 'var(--text-primary)' },
              { label: t('stats.commission'),  value: walletQuery.data.commissionWallet,  tone: 'var(--text-primary)' },
              { label: t('stats.cashback'),    value: walletQuery.data.cashbackWallet,    tone: 'var(--primary)' },
            ].map((s) => (
              <Card key={s.label} className="p-4">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {s.label}
                </div>
                <div className="text-[22px] font-bold bf-tnum mt-1" style={{ color: s.tone }}>
                  <Money minor={s.value} currency={currency} />
                </div>
              </Card>
            ))
          ) : null}
        </div>
      </section>

      {/* Security */}
      {/* The player's own stop button. The backend has had self-exclusion since
          migration 0010 and this app never called it, so stopping required
          asking support — which inverts the control. */}
      <ResponsibleGamblingSection />

      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('security.title')}
        </h2>
        <Card>
          {[
            {
              title: t('security.changePassword'),
              subtitle: t('security.changePasswordSubtitle'),
              action: <Button size="sm" variant="secondary" onClick={() => setShowChangePw(true)}>{t('security.changeButton')}</Button>,
            },
            {
              title: t('security.twoFactor'),
              subtitle: mfaEnabled ? t('security.twoFactorEnabled') : t('security.twoFactorSubtitle'),
              action: (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => (mfaEnabled ? setShowMfaDisable(true) : setShowMfaEnroll(true))}
                >
                  {mfaEnabled ? t('security.twoFactorDisableButton') : t('security.twoFactorButton')}
                </Button>
              ),
            },
          ].map((row, i, arr) => (
            <div
              key={row.title}
              className={[
                'flex items-center justify-between gap-4 py-4 px-5',
                i < arr.length - 1 ? 'border-b border-[var(--border-subtle)]' : '',
              ].join(' ')}
            >
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-[var(--text-primary)]">{row.title}</div>
                <div className="text-[12px] text-[var(--text-secondary)] mt-0.5">{row.subtitle}</div>
              </div>
              {row.action}
            </div>
          ))}
        </Card>
      </section>

      <div className="flex justify-center pt-2">
        <Button
          variant="ghost"
          onClick={() => {
            toast.success(t('logout.success', { ns: 'common' }));
            clear();
          }}
        >
          {t('signOut')}
        </Button>
      </div>

      {showChangePw && (
        <ChangePasswordModal
          subjectId={memberId}
          onClose={() => setShowChangePw(false)}
        />
      )}

      {showMfaEnroll && <TwoFactorEnrollModal onClose={() => setShowMfaEnroll(false)} />}

      {showMfaDisable && <TwoFactorDisableModal onClose={() => setShowMfaDisable(false)} />}

    </div>
  );
}
